import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { HardDrives, Plus, DownloadSimple, UploadSimple, PencilSimple, Trash, MagnifyingGlass, Buildings, X, CaretUpDown, Check, FileXls, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const UNASSIGNED = "__unassigned__";
const CI_PRESETS = ["Server", "Laptop", "Desktop", "Firewall", "Switch", "Router", "Virtual Machine", "Storage", "Printer", "Mobile", "Access Point", "Monitor", "Other"];
const STATUSES = ["In Use", "Stock", "Retired"];
const CRITS = ["High", "Medium", "Low"];

const statusColor = (s) => ({
  "In Use": "text-emerald-700 dark:text-emerald-400 border-emerald-500/40",
  "Stock": "text-amber-700 dark:text-amber-400 border-amber-500/40",
  "Retired": "text-zinc-600 dark:text-zinc-400 border-zinc-500/40",
}[s] || "border-border text-muted-foreground");
const critColor = (c) => ({
  High: "text-red-700 dark:text-red-400 border-red-500/40",
  Medium: "text-amber-700 dark:text-amber-400 border-amber-500/40",
  Low: "text-sky-700 dark:text-sky-400 border-sky-500/40",
}[c] || "border-border text-muted-foreground");

const warrantyState = (d) => {
  if (!d) return null;
  const exp = new Date(String(d).slice(0, 10) + "T00:00:00");
  if (isNaN(exp.getTime())) return null;
  const days = Math.ceil((exp - new Date()) / 86400000);
  if (days < 0) return { kind: "expired", label: "Expired", cls: "text-red-700 dark:text-red-400 border-red-500/40 bg-red-500/10" };
  if (days <= 30) return { kind: "soon", label: `${days}d left`, cls: "text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/10" };
  return null;
};

const emptyForm = () => ({
  asset_tag: "", hostname: "", ci_type: "Server", status: "In Use", user_full_name: "", user_email: "", managed_by: "",
  criticality: "Medium", manufacturer: "", model: "", serial_number: "", location: "",
  os_version: "", purchase_date: "", warranty_expiry: "", description: "",
});

export default function Assets() {
  const { user } = useAuth();
  const isStaff = ["admin", "manager", "agent"].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [wFilter, setWFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // {mode:'add'|'edit', form, id}
  const [saving, setSaving] = useState(false);
  const [ciOther, setCiOther] = useState(false);
  const [importing, setImporting] = useState(false);
  const [danger, setDanger] = useState(null); // {scope:'client'|'all', confirm:''}
  const [deleting, setDeleting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cmdb_recent_clients") || "[]"); } catch { return []; }
  });
  const fileRef = useRef(null);

  const clientLabel = (c) => (c === UNASSIGNED ? "Unassigned" : c);
  const countFor = (c) => clients.find(x => x.client === c)?.count ?? 0;

  const selectClient = (c) => {
    setActive(c);
    setPickerOpen(false);
    setRecent(prev => {
      const next = [c, ...prev.filter(x => x !== c)].slice(0, 5);
      try { localStorage.setItem("cmdb_recent_clients", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const loadClients = useCallback(() => {
    api.get("/assets/cmdb-clients").then(r => {
      setClients(r.data);
      setActive(a => a || (r.data[0]?.client ?? UNASSIGNED));
    }).catch(() => {});
  }, []);

  const loadAssets = useCallback((client) => {
    if (!client) return;
    setLoading(true);
    api.get(`/assets?client=${encodeURIComponent(client)}`)
      .then(r => setAssets(r.data)).catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { if (active) loadAssets(active); }, [active, loadAssets]);

  const refresh = () => { loadClients(); loadAssets(active); };

  const openAdd = () => { setCiOther(false); setDialog({ mode: "add", form: emptyForm(), id: null }); };
  const openEdit = (a) => {
    const f = { ...emptyForm(), ...a };
    setCiOther(!CI_PRESETS.includes(a.ci_type));
    setDialog({ mode: "edit", form: f, id: a.id });
  };
  const setField = (k, v) => setDialog(d => ({ ...d, form: { ...d.form, [k]: v } }));

  const save = async () => {
    const f = dialog.form;
    if (!f.asset_tag.trim()) { toast.error("Asset Tag is required"); return; }
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        await api.post("/assets", { ...f, client: active === UNASSIGNED ? null : active });
        toast.success("Asset added");
      } else {
        await api.patch(`/assets/${dialog.id}`, f);
        toast.success("Asset updated");
      }
      setDialog(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset ${a.asset_tag}? This cannot be undone.`)) return;
    try { await api.delete(`/assets/${a.id}`); toast.success("Asset deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const deleteClient = async (c) => {
    if (!c || c.client === UNASSIGNED) return;
    const label = clientLabel(c.client);
    const cnt = c.count || 0;
    const msg = cnt > 0
      ? `Delete client "${label}" and its ${cnt} asset(s)?\n\nThis also clears it from Routing Rules. This cannot be undone.`
      : `Delete client "${label}"?\n\nThis clears it from Routing Rules and the client registry.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/clients/${encodeURIComponent(c.client)}`, { params: { delete_assets: cnt > 0 } });
      toast.success(`Client "${label}" deleted${data.assets_deleted ? ` · ${data.assets_deleted} asset(s) removed` : ""}`);
      if (active === c.client) setActive(null);
      loadClients();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete client"); }
  };

  const runBulkDelete = async () => {
    if (!danger) return;
    setDeleting(true);
    try {
      const client = danger.scope === "all" ? "__all__" : active;
      const { data } = await api.post("/assets/bulk-delete", { client, confirm: danger.confirm });
      toast.success(`Deleted ${data.deleted} asset(s) · ${data.incidents_updated} ticket(s) & ${data.changes_updated} change(s) unlinked`);
      setDanger(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };


  const exportXlsx = async () => {
    try {
      const res = await api.get(`/assets/export?client=${encodeURIComponent(active)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `CMDB_${clientLabel(active).replace(/[^a-z0-9]/gi, "_")}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/assets/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "CMDB_Import_Template.xlsx";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("client", active);
      fd.append("file", file);
      const { data } = await api.post("/assets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported into ${clientLabel(active)} — ${data.created} added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const filtered = assets.filter(a => {
    if (wFilter !== "all") {
      const w = warrantyState(a.warranty_expiry);
      if (wFilter === "soon" && !(w && w.kind === "soon")) return false;
      if (wFilter === "expired" && !(w && w.kind === "expired")) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [a.asset_tag, a.hostname, a.ci_type, a.user_full_name, a.user_email, a.managed_by, a.serial_number, a.location]
      .some(v => (v || "").toLowerCase().includes(q));
  });

  const expiring = assets.filter(a => { const w = warrantyState(a.warranty_expiry); return w && w.kind === "soon"; }).length;
  const expired = assets.filter(a => { const w = warrantyState(a.warranty_expiry); return w && w.kind === "expired"; }).length;

  return (
    <div className="p-6" data-testid="assets-view">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Configuration Management DB</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Assets (CMDB)</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">A separate CMDB per client. Add clients in <span className="font-mono text-primary">Routing Rules</span> — each gets its own asset register you can manage, import and export.</p>
        </div>
      </div>

      {/* Client selector */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="cmdb-client-selector">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="cmdb-client-trigger"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/50 bg-primary/5 text-sm font-mono text-foreground hover:border-primary transition-colors min-w-[240px]"
            >
              <Buildings size={14} className="text-primary shrink-0" />
              <span className="truncate">{active ? clientLabel(active) : "Select client…"}</span>
              {active && <span className="px-1.5 rounded-sm bg-primary/15 text-primary text-xs">{countFor(active)}</span>}
              <CaretUpDown size={14} className="ml-auto text-muted-foreground shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[300px] rounded-sm" align="start">
            <Command>
              <CommandInput placeholder="Search clients…" data-testid="cmdb-client-search" className="text-sm" />
              <CommandList>
                <CommandEmpty>No clients found.</CommandEmpty>
                <CommandGroup>
                  {clients.map(c => (
                    <CommandItem
                      key={c.client}
                      value={clientLabel(c.client)}
                      onSelect={() => selectClient(c.client)}
                      data-testid={`cmdb-client-option-${c.client}`}
                      className="text-sm font-mono flex items-center gap-2"
                    >
                      <Check size={14} className={active === c.client ? "opacity-100 text-primary" : "opacity-0"} />
                      <Buildings size={13} className="text-muted-foreground" />
                      <span className="truncate flex-1">{clientLabel(c.client)}</span>
                      <span className="px-1.5 rounded-sm bg-muted text-muted-foreground text-xs">{c.count}</span>
                      {(user?.role === "admin" || user?.role === "manager") && c.client !== UNASSIGNED && (
                        <button
                          data-testid={`cmdb-client-delete-${c.client}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteClient(c); }}
                          title={`Delete client "${clientLabel(c.client)}"`}
                          className="ml-1 text-muted-foreground hover:text-red-500 transition-colors"
                        >
                          <Trash size={13} />
                        </button>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Recently viewed */}
        {recent.filter(c => c !== active && clients.some(x => x.client === c)).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="cmdb-recent-clients">
            <span className="text-[10px] font-mono uppercase text-muted-foreground">Recent</span>
            {recent.filter(c => c !== active && clients.some(x => x.client === c)).slice(0, 4).map(c => (
              <button
                key={c}
                data-testid={`cmdb-recent-${c}`}
                onClick={() => selectClient(c)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-border text-xs font-mono text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
              >
                {clientLabel(c)}
                <span className="px-1 rounded-sm bg-muted">{countFor(c)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Card className="border-border rounded-sm overflow-hidden">
        {(expiring > 0 || expired > 0) && (
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-500/30 bg-amber-500/5" data-testid="warranty-banner">
            <WarningCircle size={15} className="text-amber-600 dark:text-amber-400" weight="fill" />
            <span className="text-xs font-mono text-amber-700 dark:text-amber-300">
              {expiring > 0 && <>{expiring} asset{expiring > 1 ? "s" : ""} with warranty expiring within 30 days</>}
              {expiring > 0 && expired > 0 && " · "}
              {expired > 0 && <>{expired} already expired</>}
            </span>
          </div>
        )}
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-muted/20">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="asset-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assets…" className="pl-8 h-9 rounded-sm text-sm" />
          </div>
          <div className="flex items-center rounded-sm border border-border overflow-hidden" data-testid="warranty-filter">
            {[["all", "All"], ["soon", "Expiring"], ["expired", "Expired"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`warranty-filter-${v}`}
                onClick={() => setWFilter(v)}
                className={`px-3 h-9 text-[10px] font-mono uppercase tracking-wide transition-colors ${wFilter === v ? (v === "expired" ? "bg-red-500/15 text-red-700 dark:text-red-400" : v === "soon" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-primary/10 text-primary") : "text-muted-foreground hover:bg-accent/40"}`}
              >{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onImportFile} data-testid="asset-import-input" />
            <Button data-testid="asset-template-btn" variant="outline" size="sm" className="rounded-sm font-mono uppercase text-[10px] h-9" onClick={downloadTemplate}>
              <FileXls size={14} className="mr-1.5" />Template
            </Button>
            <Button data-testid="asset-import-btn" variant="outline" size="sm" className="rounded-sm font-mono uppercase text-[10px] h-9" disabled={importing} onClick={() => fileRef.current?.click()}>
              <UploadSimple size={14} className="mr-1.5" />{importing ? "Importing…" : "Import"}
            </Button>
            <Button data-testid="asset-export-btn" variant="outline" size="sm" className="rounded-sm font-mono uppercase text-[10px] h-9" onClick={exportXlsx}>
              <DownloadSimple size={14} className="mr-1.5" />Export
            </Button>
            {isStaff && (
              <Button data-testid="asset-add-btn" size="sm" className="rounded-sm font-mono uppercase text-[10px] h-9" onClick={openAdd}>
                <Plus size={14} className="mr-1.5" />Add asset
              </Button>
            )}
            {user?.role === "admin" && (
              <Button data-testid="asset-delete-all-btn" variant="outline" size="sm"
                className="rounded-sm font-mono uppercase text-[10px] h-9 border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                onClick={() => setDanger({ scope: "client", confirm: "" })}>
                <Trash size={14} className="mr-1.5" />Delete all
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                <th className="text-left px-4 py-3">Asset Tag</th>
                <th className="text-left px-4 py-3">Hostname</th>
                <th className="text-left px-4 py-3">CI Type</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Criticality</th>
                <th className="text-left px-4 py-3">User's Full Name</th>
                <th className="text-left px-4 py-3">Email ID</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Warranty</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b border-border/60 hover:bg-accent/20 cursor-pointer" data-testid={`asset-row-${a.asset_tag}`} onClick={() => openEdit(a)}>
                  <td className="px-4 py-3 font-mono text-xs text-primary">{a.asset_tag}</td>
                  <td className="px-4 py-3">{a.hostname || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{a.ci_type}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${statusColor(a.status)}`}>{a.status}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${critColor(a.criticality)}`}>{a.criticality}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{a.user_full_name || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{a.user_email || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{a.location || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {a.warranty_expiry ? (() => {
                      const w = warrantyState(a.warranty_expiry);
                      return (
                        <div className="flex items-center gap-1.5" data-testid={`asset-warranty-${a.asset_tag}`}>
                          <span className="text-xs font-mono text-muted-foreground">{String(a.warranty_expiry).slice(0, 10)}</span>
                          {w && <span className={`text-[10px] font-mono px-1.5 py-0.5 border uppercase rounded-sm ${w.cls}`} data-testid={`asset-warranty-flag-${a.asset_tag}`}>{w.label}</span>}
                        </div>
                      );
                    })() : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <Button data-testid={`asset-edit-${a.asset_tag}`} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-sm" onClick={() => openEdit(a)}><PencilSimple size={14} /></Button>
                      {isStaff && <Button data-testid={`asset-delete-${a.asset_tag}`} size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-500/10" onClick={() => remove(a)}><Trash size={14} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-muted-foreground text-sm">
                  {search ? "No assets match your search." : `No assets in ${clientLabel(active || "")} yet. Add one or import an .xlsx file.`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="asset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase text-sm tracking-widest">
              <HardDrives size={16} className="text-primary" />
              {dialog?.mode === "add" ? `Add asset · ${clientLabel(active || "")}` : `Edit ${dialog?.form?.asset_tag}`}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Asset Tag *"><Input data-testid="f-asset_tag" value={dialog.form.asset_tag} onChange={e => setField("asset_tag", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Hostname"><Input data-testid="f-hostname" value={dialog.form.hostname} onChange={e => setField("hostname", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="CI Type">
                {ciOther ? (
                  <div className="flex gap-2">
                    <Input data-testid="f-ci_type-other" value={dialog.form.ci_type} onChange={e => setField("ci_type", e.target.value)} className="rounded-sm" placeholder="Custom type" />
                    <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => { setCiOther(false); setField("ci_type", "Server"); }}><X size={14} /></Button>
                  </div>
                ) : (
                  <Select value={dialog.form.ci_type} onValueChange={(v) => { if (v === "Other") { setCiOther(true); setField("ci_type", ""); } else setField("ci_type", v); }}>
                    <SelectTrigger data-testid="f-ci_type" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{CI_PRESETS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Fld>
              <Fld label="Status">
                <Select value={dialog.form.status} onValueChange={(v) => setField("status", v)}>
                  <SelectTrigger data-testid="f-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Criticality">
                <Select value={dialog.form.criticality} onValueChange={(v) => setField("criticality", v)}>
                  <SelectTrigger data-testid="f-criticality" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{CRITS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="User's Full Name"><Input data-testid="f-user_full_name" value={dialog.form.user_full_name} onChange={e => setField("user_full_name", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Email ID"><Input data-testid="f-user_email" type="email" value={dialog.form.user_email} onChange={e => setField("user_email", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Managed by"><Input data-testid="f-managed_by" value={dialog.form.managed_by} onChange={e => setField("managed_by", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Manufacturer"><Input data-testid="f-manufacturer" value={dialog.form.manufacturer} onChange={e => setField("manufacturer", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Model"><Input data-testid="f-model" value={dialog.form.model} onChange={e => setField("model", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Serial Number"><Input data-testid="f-serial_number" value={dialog.form.serial_number} onChange={e => setField("serial_number", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Location"><Input data-testid="f-location" value={dialog.form.location} onChange={e => setField("location", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="OS / Version"><Input data-testid="f-os_version" value={dialog.form.os_version} onChange={e => setField("os_version", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Purchase Date"><Input data-testid="f-purchase_date" type="date" value={dialog.form.purchase_date || ""} onChange={e => setField("purchase_date", e.target.value)} className="rounded-sm" /></Fld>
              <Fld label="Warranty Expiry"><Input data-testid="f-warranty_expiry" type="date" value={dialog.form.warranty_expiry || ""} onChange={e => setField("warranty_expiry", e.target.value)} className="rounded-sm" /></Fld>
              <div className="sm:col-span-2">
                <Fld label="Description"><Textarea data-testid="f-description" value={dialog.form.description} onChange={e => setField("description", e.target.value)} className="rounded-sm min-h-[70px]" /></Fld>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="asset-save-btn" className="rounded-sm text-xs font-mono uppercase" onClick={save} disabled={saving}>{saving ? "Saving…" : (dialog?.mode === "add" ? "Add asset" : "Save changes")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger zone: bulk delete assets */}
      <Dialog open={!!danger} onOpenChange={(o) => !o && setDanger(null)}>
        <DialogContent className="rounded-sm" data-testid="asset-delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400"><WarningCircle size={18} weight="fill" /> Delete assets — irreversible</DialogTitle>
          </DialogHeader>
          {danger && (
            <div className="space-y-4">
              <div className="flex items-center rounded-sm border border-border overflow-hidden text-xs font-mono uppercase" data-testid="delete-scope-toggle">
                <button data-testid="delete-scope-client" onClick={() => setDanger(d => ({ ...d, scope: "client" }))}
                  className={`flex-1 px-3 py-2 transition-colors ${danger.scope === "client" ? "bg-red-500/15 text-red-700 dark:text-red-400" : "text-muted-foreground hover:bg-accent/40"}`}>
                  This client
                </button>
                <button data-testid="delete-scope-all" onClick={() => setDanger(d => ({ ...d, scope: "all" }))}
                  className={`flex-1 px-3 py-2 transition-colors ${danger.scope === "all" ? "bg-red-500/15 text-red-700 dark:text-red-400" : "text-muted-foreground hover:bg-accent/40"}`}>
                  All clients
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                {danger.scope === "all"
                  ? <>This will permanently delete <b className="text-foreground">every asset across all clients</b> and remove their links from all tickets and changes.</>
                  : <>This will permanently delete <b className="text-foreground">all assets in {clientLabel(active)}</b> ({countFor(active)} asset{countFor(active) === 1 ? "" : "s"}) and remove their links from tickets and changes.</>}
                {" "}This cannot be undone.
              </p>
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Type DELETE to confirm</Label>
                <Input data-testid="delete-confirm-input" value={danger.confirm} onChange={e => setDanger(d => ({ ...d, confirm: e.target.value }))} placeholder="DELETE" className="mt-1 rounded-sm font-mono" autoFocus />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setDanger(null)}>Cancel</Button>
            <Button data-testid="delete-all-confirm-btn" onClick={runBulkDelete} disabled={deleting || danger?.confirm !== "DELETE"}
              className="rounded-sm text-xs font-mono uppercase bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting…" : "Delete assets"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div>
    <Label className="text-[10px] font-mono uppercase text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
